import { Component, OnInit, ChangeDetectionStrategy, Input, ChangeDetectorRef } from '@angular/core';
import { Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { NotificationsService } from 'api/notifications.api';
import moment from 'moment';
import { auditTime, filter, startWith, take } from 'rxjs/operators';
import { HeatmapClickEvent } from 'shared/cal-heatmap/cal-heatmap.component';
import { Subject } from 'rxjs';
import { HasCapabilityCategoryPipe } from 'shared/stations-service/station-capabilities.pipe';
import { ReportingQueryService } from '../../../../../services/reporting-query.service';

@Component({
    selector: 'ce-tile-usage',
    templateUrl: './tile-usage.component.html',
    styleUrls: ['./tile-usage.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
@UntilDestroy()
export class TileUsageComponent implements OnInit {
    @Input() stationIds: string[];
    @Input() calendarMonths = 3;

    loading = true;

    title = 'Usage';

    calendarHeatmapData: { timestamp: string; value: number }[];
    calendarHeatmapDataStart = moment()
        .add(-(this.calendarMonths - 1), 'months')
        .startOf('month')
        .toDate();

    totalEnergykWh: number;
    totalEnergykWhPast30days: number;
    peakPowerkW: number;

    locationHasNonKempowerChargers = false;

    private onDestroy$ = new Subject<void>();

    constructor(
        private cdRef: ChangeDetectorRef,
        private reportingQuery: ReportingQueryService,
        private notifications: NotificationsService,
        private router: Router,
        private hasCapabilityCategoryPipe: HasCapabilityCategoryPipe
    ) {}

    async ngOnInit() {
        // Subscribe to station
        this.notifications.connected$
            .pipe(
                filter((c) => c === true),
                untilDestroyed(this),
                take(1)
            )
            .subscribe(() => {
                this.notifications.subscribeUntil({ stationIds: this.stationIds }, this.onDestroy$);
            });

        if (this.stationIds) {
            for (const stationId of this.stationIds) {
                if (!this.hasCapabilityCategoryPipe.transform(stationId, 'KEMPOWER_NATIVE')) {
                    this.locationHasNonKempowerChargers = true;
                    break;
                }
            }
        }

        this.notifications.notification$
            .pipe(
                filter(
                    (n) =>
                        n.type === 'ChargingStationUpdated' &&
                        (this.stationIds || []).includes(n.stationId) &&
                        n.changedSchemas?.includes('tx')
                ),
                // One minute
                auditTime(60 * 1000),
                startWith([]),
                untilDestroyed(this)
            )
            .subscribe(async () => {
                await this.updateFigures();
            });
    }

    ngOnDestroy() {
        this.onDestroy$.next();
    }

    dateClicked(event: HeatmapClickEvent) {
        // !FIXME: now do not navigate if this is locationId (sessions list only displays txs of 1 stationId, or all txs)
        if (this.stationIds && this.stationIds.length > 1) {
            return;
        }
        const startDate = moment(event.date).startOf('d').toISOString();
        const endDate = moment(event.date).endOf('d').toISOString();
        const d = this.stationIds?.length && this.stationIds[0] ? ['txs', this.stationIds[0]] : ['txs'];

        this.router.navigate(['', { outlets: { d } }], {
            queryParams: { 'filter.startDate': startDate, 'filter.endDate': endDate },
        });
    }

    async updateFigures() {
        await Promise.all([
            this.fetchTotalEnergy(),
            this.fetchTotalEnergyPast30days(),
            this.fetchPeakPower(),
            this.fetchCalendarHeatMap(),
        ]).catch((e) => console.error(e));
        this.loading = false;

        this.cdRef.markForCheck();
    }

    async fetchTotalEnergy() {
        return this.reportingQuery
            .query(
                {
                    query: {
                        indexName: 'schema-tx',
                        size: 0,
                        parameters: [
                            { term: { schema: { value: 'tx' } } },
                            ...(this.stationIds?.length ? [{ terms: { stationId: this.stationIds } }] : []),
                        ],
                        aggregations: { totalkWh: { sum: { field: 'chargedEnergyKwh' } } },
                        disableEsParser: true,
                    },
                },
                { src: 'usage-energy-total' }
            )
            .then((res) => {
                this.totalEnergykWh = res.response.totalkWh.value;
            })
            .catch((e) => {
                console.error(e);
            });
    }

    async fetchTotalEnergyPast30days() {
        return this.reportingQuery
            .query(
                {
                    query: {
                        indexName: 'schema-tx',
                        size: 0,
                        parameters: [
                            { term: { schema: { value: 'tx' } } },
                            ...(this.stationIds?.length ? [{ terms: { stationId: this.stationIds } }] : []),
                            { range: { startTime: { gte: 'now-30d/d' } } },
                        ],
                        aggregations: { totalkWh30: { sum: { field: 'chargedEnergyKwh' } } },
                        disableEsParser: true,
                    },
                },
                { src: 'usage-energy-30d' }
            )
            .then((res) => {
                this.totalEnergykWhPast30days = res.response.totalkWh30.value;
            })
            .catch((e) => {
                console.error(e);
            });
    }

    async fetchPeakPower() {
        if (this.locationHasNonKempowerChargers) {
            return this.reportingQuery
                .query(
                    {
                        query: {
                            indexName: 'schema-tx',
                            size: 0,
                            parameters: [
                                { term: { schema: { value: 'tx' } } },
                                ...(this.stationIds?.length ? [{ terms: { stationId: this.stationIds } }] : []),
                                { range: { startTime: { gte: 'now-30d/d' } } },
                            ],
                            aggregations: { maxPowerKw: { max: { field: 'maxPowerKw' } } },
                            disableEsParser: true,
                        },
                    },
                    { src: 'usage-peak-power' }
                )
                .then((res) => {
                    this.peakPowerkW = res?.response?.maxPowerKw?.value;
                })
                .catch((e) => {
                    console.error(e);
                });
        } else {
            return this.reportingQuery
                .query(
                    {
                        query: {
                            indexName: 'schema-systemmetricshistory',
                            size: 0,
                            parameters: [
                                { term: { schema: 'systemMetricsHistory' } },
                                ...(this.stationIds?.length ? [{ terms: { stationId: this.stationIds } }] : []),
                                { range: { timestamp: { gte: 'now-30d/d' } } },
                            ],
                            disableEsParser: true,
                            aggregations: {
                                power: {
                                    max: {
                                        field: 'metrics.powerStats.max',
                                    },
                                },
                            },
                        },
                    },
                    { src: 'usage-peak-power' }
                )
                .then((res) => {
                    this.peakPowerkW = (res?.response?.power?.value || 0) / 1000;
                })
                .catch((e) => {
                    console.error(e);
                });
        }
    }

    async fetchCalendarHeatMap() {
        const start = moment()
            .add(-(this.calendarMonths - 1), 'months')
            .startOf('month');
        return this.reportingQuery
            .query(
                {
                    query: {
                        indexName: 'schema-tx',
                        size: 0,
                        parameters: [
                            { term: { schema: { value: 'tx' } } },
                            ...(this.stationIds?.length ? [{ terms: { stationId: this.stationIds } }] : []),
                            { range: { startTime: { gte: start.toISOString() } } },
                        ],
                        disableEsParser: true,
                        aggregations: {
                            days: {
                                date_histogram: {
                                    field: 'startTime',
                                    calendar_interval: '1d',
                                },
                                aggs: {
                                    chargedEnergyKwh: {
                                        sum: {
                                            field: 'chargedEnergyKwh',
                                        },
                                    },
                                },
                            },
                        },
                    },
                },
                { src: 'usage-calendar' }
            )
            .then((res) => {
                const data: { timestamp: string; value: number }[] = [];

                for (const day of res.response.days.buckets) {
                    data.push({
                        timestamp: day.key_as_string,
                        value: day.chargedEnergyKwh.value,
                    });
                }

                this.calendarHeatmapData = data;
                this.calendarHeatmapDataStart = start.toDate();
            })
            .catch((e) => {
                console.error(e);
            });
    }
}
